import AuthPageFrame from '../AuthPageFrame';
import ExpiredRecoveryLink from './components/ExpiredRecoveryLink';
import LoadingIndicator from '@/components/ui/LoadingIndicator';
import { Button } from '@/components/ui/button';
import { Eye, EyeOff } from 'lucide-react';
import { FormEvent, useState } from 'react';
import { getNewPasswordError, usePasswordPolicy } from '../../model/passwordPolicy';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Link, useNavigate } from 'react-router-dom';
import { RECOVERY_SUPPORT_EMAIL } from '../../model/recoverySupport';
import { useAuthStore } from '../../model/authStore';
import { usePasswordRecovery } from '../../model/usePasswordRecovery';
import { useQueryClient } from '@tanstack/react-query';

const ResetPasswordForm = () => {
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);
  const [isNewPasswordVisible, setIsNewPasswordVisible] = useState(false);
  const [validationError, setValidationError] = useState('');
  const {
    data: dataGetPasswordPolicy,
    isError: isErrorGetPasswordPolicy,
    refetch: refetchGetPasswordPolicy
  } = usePasswordPolicy();
  const flow = usePasswordRecovery();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const onGoToLogin = (): void => {
    useAuthStore.getState().clearLocalSession();
    void queryClient.cancelQueries();
    queryClient.clear();
    navigate('/login', { replace: true });
  };

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const error = getNewPasswordError(password, dataGetPasswordPolicy)
      || (password !== confirmation ? 'Пароли не совпадают' : '');
    setValidationError(error);
    if (error) return;
    flow.submit(password);
    setPassword('');
    setConfirmation('');
  };

  if (flow.view === 'invalid') {
    return (
      <AuthPageFrame subtitle="Восстановление пароля">
        <ExpiredRecoveryLink onGoToLogin={onGoToLogin} />
      </AuthPageFrame>
    );
  }

  if (flow.view === 'loading' || flow.view === 'processing') {
    return (
      <AuthPageFrame subtitle="Восстановление пароля">
        <div
          className="flex flex-col items-center gap-4"
          aria-busy
          role="status"
        >
          <h1 className="text-center font-sans text-2xl font-semibold normal-case leading-8 tracking-normal">
            {flow.view === 'processing' ? 'Проверяем результат восстановления…' : 'Проверяем ссылку'}
          </h1>
          <LoadingIndicator size="lg" />
        </div>
      </AuthPageFrame>
    );
  }

  const title = flow.view === 'completed' ? 'Пароль изменён'
    : flow.view === 'support' ? 'Нужна помощь поддержки'
    : flow.view === 'form' ? 'Задайте новый пароль'
    : flow.view === 'missing' ? 'Откройте ссылку из письма'
    : flow.view === 'unavailable' ? 'Не удалось проверить ссылку' : 'Результат не подтверждён';

  return (
    <AuthPageFrame subtitle="Восстановление пароля">
      <div className="space-y-4" aria-busy={flow.isBusy}>
        <h1
          className="text-center font-sans text-2xl font-semibold normal-case leading-8 tracking-normal"
          aria-live="polite"
        >
          {title}
        </h1>
        {flow.view === 'form' ? (
          <form
            className="space-y-4"
            noValidate
            onSubmit={onSubmit}
          >
            {isErrorGetPasswordPolicy && (
              <div className="space-y-2 text-sm" role="alert">
                <p>Не удалось загрузить требования к паролю.</p>
                <Button
                  onClick={() => void refetchGetPasswordPolicy()}
                  type="button"
                  variant="tertiary"
                >
                  Повторить загрузку
                </Button>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-sm font-normal leading-5 text-muted-foreground" htmlFor="new-password">
                Новый пароль
              </Label>
              <Input
                autoComplete="new-password"
                disabled={!dataGetPasswordPolicy || flow.isBusy}
                id="new-password"
                onChange={(event) => setPassword(event.target.value)}
                required
                rightIcon={(
                  <button
                    aria-label={isNewPasswordVisible ? 'Скрыть новый пароль' : 'Показать новый пароль'}
                    aria-pressed={isNewPasswordVisible}
                    onClick={() => setIsNewPasswordVisible((value) => !value)}
                    type="button"
                  >
                    {isNewPasswordVisible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </button>
                )}
                type={isNewPasswordVisible ? 'text' : 'password'}
                value={password}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-normal leading-5 text-muted-foreground" htmlFor="confirm-password">
                Подтвердите пароль
              </Label>
              <Input
                autoComplete="new-password"
                disabled={!dataGetPasswordPolicy || flow.isBusy}
                id="confirm-password"
                onChange={(event) => setConfirmation(event.target.value)}
                required
                rightIcon={(
                  <button
                    aria-label={isConfirmPasswordVisible ? 'Скрыть подтверждение пароля' : 'Показать подтверждение пароля'}
                    aria-pressed={isConfirmPasswordVisible}
                    onClick={() => setIsConfirmPasswordVisible((value) => !value)}
                    type="button"
                  >
                    {isConfirmPasswordVisible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </button>
                )}
                type={isConfirmPasswordVisible ? 'text' : 'password'}
                value={confirmation}
              />
            </div>
            {(validationError || flow.message) && (
              <p className="text-sm text-[hsl(var(--error-text))]" role="alert">{validationError || flow.message}</p>
            )}
            <Button
              className="w-full"
              disabled={!dataGetPasswordPolicy || flow.isBusy}
              size="lg"
              type="submit"
            >
              Сохранить пароль
            </Button>
          </form>
        ) : (
          <>
            {flow.view === 'missing' && (
              <p className="text-sm">
                Откройте ссылку из письма ещё раз, чтобы продолжить восстановление или проверить его результат.
                {' '}Если письма нет, запросите новую ссылку.
              </p>
            )}
            {flow.view === 'completed' && <p className="text-sm">Теперь войдите с новым паролем.</p>}
            {flow.view === 'support' && (
              <p className="text-sm">
                Не удалось подтвердить завершение восстановления. Обратитесь в поддержку White Line:
                {' '}<a className="inline-block max-w-full break-words underline underline-offset-4" href={`mailto:${RECOVERY_SUPPORT_EMAIL}`}>
                  {RECOVERY_SUPPORT_EMAIL}
                </a>.
              </p>
            )}
            {flow.view === 'unconfirmed' && (
              <p className="text-sm">
                Не удалось подтвердить изменение пароля. Попробуйте войти с новым паролем.
                {' '}Если войти не получается, обратитесь в поддержку White Line:
                {' '}<a className="inline-block max-w-full break-words underline underline-offset-4" href={`mailto:${RECOVERY_SUPPORT_EMAIL}`}>
                  {RECOVERY_SUPPORT_EMAIL}
                </a>.
              </p>
            )}
            {flow.view === 'unavailable' && flow.message && (
              <p className="text-sm text-[hsl(var(--error-text))]" role="alert">{flow.message}</p>
            )}
            {flow.view === 'unavailable' && (
              <Button
                className="w-full"
                disabled={flow.isBusy || flow.secondsLeft > 0}
                onClick={() => void flow.inspect()}
                size="lg"
                type="button"
              >
                Проверить ссылку ещё раз
              </Button>
            )}
          </>
        )}
        {flow.view === 'completed' || flow.view === 'unconfirmed' ? (
          <Button
            className="w-full"
            onClick={onGoToLogin}
            size="lg"
            type="button"
          >
            {flow.view === 'completed' ? 'Войти в восстановленный аккаунт' : 'Войти с новым паролем'}
          </Button>
        ) : flow.view !== 'support' ? (
          <div className="space-y-3 text-center text-sm">
            {flow.view === 'missing' && (
              <Button
                className="w-full"
                asChild
                variant="tertiary"
              >
                <Link to="/forgot-password">Запросить ссылку</Link>
              </Button>
            )}
            <Button
              className="w-full"
              onClick={onGoToLogin}
              type="button"
              variant="tertiary"
            >
              Вернуться ко входу
            </Button>
          </div>
        ) : null}
      </div>
    </AuthPageFrame>
  );
};

export default ResetPasswordForm;